// src/pages/RegisterPage.tsx
import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import { fetchBots } from "../api/bots";

declare global {
  interface Window {
    google?: any;
  }
}

type PasswordRule =
  | "minLength"
  | "uppercase"
  | "lowercase"
  | "digit"
  | "special";

function validatePassword(password: string): PasswordRule[] {
  const errors: PasswordRule[] = [];

  if (password.length < 8) {
    errors.push("minLength");
  }
  if (!/[A-Z]/.test(password)) {
    errors.push("uppercase");
  }
  if (!/[a-z]/.test(password)) {
    errors.push("lowercase");
  }
  if (!/[0-9]/.test(password)) {
    errors.push("digit");
  }
  if (!/[^A-Za-z0-9]/.test(password)) {
    errors.push("special");
  }

  return errors;
}

const RegisterPage: React.FC = () => {
  const { register, loginWithGoogle, isLoading } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const googleButtonRef = useRef<HTMLDivElement | null>(null);

  const passwordErrors = validatePassword(password);
  const passwordsMatch =
    password === confirmPassword || confirmPassword === "";

  const redirectAfterLogin = async () => {
    try {
      const bots = await fetchBots();

      if (!bots || bots.length === 0) {
        // No bots yet → go straight to onboarding
        navigate("/onboarding/bots/new", { replace: true });
      } else {
        // At least one bot → go to bots list
        navigate("/app/bots", { replace: true });
      }
    } catch (err) {
      console.error(
        "Failed to fetch bots after register/login, falling back to /app/bots",
        err
      );
      // Safe fallback if something goes wrong
      navigate("/app/bots", { replace: true });
    }
  };

  const handleSubmit: React.FormEventHandler = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (password !== confirmPassword) {
      setError(t("register.errorPasswordMismatch"));
      return;
    }

    if (passwordErrors.length > 0) {
      setError(t("register.errorPasswordRequirements"));
      return;
    }

    try {
      await register(email, password);
      setSuccess(true);
      setPassword("");
      setConfirmPassword("");
    } catch (err: any) {
      console.error(err);
      setError(err.message || t("register.errorGeneric"));
    }
  };

  useEffect(() => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!clientId) {
      console.warn("VITE_GOOGLE_CLIENT_ID is not set; Google auth disabled.");
      return;
    }
    if (!window.google || !googleButtonRef.current) {
      return;
    }

    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: async (response: any) => {
        const idToken = response.credential;
        if (!idToken) return;

        try {
          setError(null);
          setSuccess(false);

          // On the register page we assume no MFA:
          // if loginWithGoogle throws, we show an error;
          // otherwise we redirect like a normal login.
          const result = await loginWithGoogle(idToken);

          // If your loginWithGoogle returns a shape like { kind: "success" | "mfa" }
          // we treat anything that's not "success" as unsupported here.
          if (result && (result as any).kind && (result as any).kind !== "success") {
            setError(
              t("register.googleErrorMfaNotSupported", {
                defaultValue:
                  "Your account requires additional verification. Please use the Login page."
              })
            );
            return;
          }

          await redirectAfterLogin();
        } catch (err: any) {
          console.error(err);
          setError(err.message || t("register.googleError"));
        }
      }
    });

    window.google.accounts.id.renderButton(googleButtonRef.current, {
      theme: "outline",
      size: "large"
    });
  }, [loginWithGoogle, t, navigate]);

  const hasError = (rule: PasswordRule) => passwordErrors.includes(rule);

  return (
    <section className="auth-landing">
      <div className="lp-container">
        <div className="auth-layout">
          {/* Copy a sinistra */}
          <div className="auth-copy">
            <h1 className="auth-title">{t("register.pageTitle")}</h1>
            <p className="auth-subtitle">{t("register.subtitle")}</p>
            <ul className="auth-bullets">
              <li>{t("register.bullets.b1")}</li>
              <li>{t("register.bullets.b2")}</li>
              <li>{t("register.bullets.b3")}</li>
            </ul>
          </div>

          {/* Card con il form */}
          <div className="auth-card">
            <h2 className="auth-card-title">{t("register.cardTitle")}</h2>

            <form className="form auth-form" onSubmit={handleSubmit}>
              {error && (
                <div className="form-error onboarding-form-error-txt auth-form-error">
                  {error}
                </div>
              )}
              {success && (
                <div className="form-success onboarding-form-success-txt auth-form-success">
                  {t("register.success")}
                </div>
              )}

              <label className="form-field">
                <span>{t("register.email")}</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </label>

              <label className="form-field">
                <span>{t("register.password")}</span>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </label>

              {/* Checklist requisiti password */}
              <div className="password-requirements">
                <p className="pw-title">{t("register.pwTitle")}</p>
                <ul>
                  <li
                    className={
                      hasError("minLength")
                        ? "pw-rule pw-rule-bad"
                        : "pw-rule pw-rule-ok"
                    }
                  >
                    {t("register.pwRules.minLength")}
                  </li>
                  <li
                    className={
                      hasError("uppercase")
                        ? "pw-rule pw-rule-bad"
                        : "pw-rule pw-rule-ok"
                    }
                  >
                    {t("register.pwRules.uppercase")}
                  </li>
                  <li
                    className={
                      hasError("lowercase")
                        ? "pw-rule pw-rule-bad"
                        : "pw-rule pw-rule-ok"
                    }
                  >
                    {t("register.pwRules.lowercase")}
                  </li>
                  <li
                    className={
                      hasError("digit")
                        ? "pw-rule pw-rule-bad"
                        : "pw-rule pw-rule-ok"
                    }
                  >
                    {t("register.pwRules.digit")}
                  </li>
                  <li
                    className={
                      hasError("special")
                        ? "pw-rule pw-rule-bad"
                        : "pw-rule pw-rule-ok"
                    }
                  >
                    {t("register.pwRules.special")}
                  </li>
                </ul>
              </div>

              <label className="form-field">
                <span>{t("register.passwordConfirm")}</span>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </label>
              {!passwordsMatch && (
                <div className="field-error">
                  {t("register.errorPasswordMismatch")}
                </div>
              )}

              <button
                className="lp-btn lp-btn-primary auth-submit"
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? t("register.submitting") : t("register.submit")}
              </button>
            </form>

            <div className="auth-divider">
              <span>{t("login.divider")}</span>
            </div>

            <div className="auth-google">
              <div ref={googleButtonRef} />
            </div>

            <p className="auth-legal" style={{ color: "black" }}>
              {t("auth.legalNotice.p1")}{" "}
              <Link to="/terms">{t("auth.legalNotice.terms")}</Link>{" "}
              {t("auth.legalNotice.and")}{" "}
              <Link to="/policy">{t("auth.legalNotice.privacy")}</Link>.
            </p>

            <p className="auth-switch">
              {t("register.alreadyAccount")}{" "}
              <Link to="/login">{t("register.login")}</Link>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RegisterPage;
